'use client';

import { Play } from 'lucide-react';

import { useCallback, useEffect, useRef, useState } from 'react';

import Image from 'next/image';

const PRODUCT_DEMO_VIDEO_ID = 'Otkj-EaAwho';
const YOUTUBE_ORIGIN = 'https://www.youtube-nocookie.com';
const POSTER_URL = '/images/landing/product-demo-poster.jpg';

type PlaybackState = 'idle' | 'playing' | 'paused' | 'ended';

// YouTube's player states, as reported over postMessage by the embed's JS API.
const PLAYER_STATE: Record<number, PlaybackState> = {
	0: 'ended',
	1: 'playing',
	2: 'paused',
};

export const ProductDemoVideo = () => {
	const [playback, setPlayback] = useState<PlaybackState>('idle');
	const iframeRef = useRef<HTMLIFrameElement>(null);

	const postCommand = useCallback((func: 'playVideo' | 'pauseVideo') => {
		iframeRef.current?.contentWindow?.postMessage(
			JSON.stringify({ event: 'command', func, args: [] }),
			YOUTUBE_ORIGIN
		);
	}, []);

	const handleToggle = useCallback(() => {
		setPlayback((current) => {
			if (current !== 'idle') postCommand(current === 'playing' ? 'pauseVideo' : 'playVideo');
			return 'playing';
		});
	}, [postCommand]);

	// Opt in to state updates so the toggle stays in sync with YouTube's own controls.
	const handleIframeLoad = useCallback(() => {
		iframeRef.current?.contentWindow?.postMessage(
			JSON.stringify({ event: 'listening', id: PRODUCT_DEMO_VIDEO_ID, channel: 'widget' }),
			YOUTUBE_ORIGIN
		);
	}, []);

	useEffect(() => {
		if (playback === 'idle') return;

		const handleMessage = (event: MessageEvent) => {
			if (event.origin !== YOUTUBE_ORIGIN || event.source !== iframeRef.current?.contentWindow) {
				return;
			}

			try {
				const payload = JSON.parse(event.data);
				const nextState = PLAYER_STATE[payload?.info?.playerState];
				if (nextState) setPlayback(nextState);
			} catch {
				// Non-JSON chatter from the embed; nothing to sync.
			}
		};

		window.addEventListener('message', handleMessage);
		return () => window.removeEventListener('message', handleMessage);
	}, [playback]);

	return (
		<section
			className="landing-width mb-16 mt-6 flex w-full flex-col items-center md:mb-20 md:mt-8"
			aria-labelledby="product-demo-heading"
		>
			<h2
				id="product-demo-heading"
				className="sr-only"
			>
				Product demo
			</h2>

			{playback === 'idle' ? (
				<div className="relative w-full max-w-[352px]">
					<div
						aria-hidden="true"
						className="pointer-events-none absolute left-1/2 top-[36%] z-0 h-[min(100vw,48rem)] w-[min(100vw,48rem)] -translate-x-1/2 -translate-y-1/2 bg-[radial-gradient(circle_at_center,rgb(37_99_235_/_0.32)_0%,rgb(59_130_246_/_0.14)_36%,transparent_74%)]"
					/>
					<button
						type="button"
						onClick={handleToggle}
						aria-label="Play the Binary ATS product demo"
						className="group relative z-[1] aspect-video w-full overflow-hidden rounded-xl border border-slate-200 bg-black"
					>
						{/* LCP element — served same-origin and priority-loaded so it isn't gated on a third-party handshake. */}
						<Image
							src={POSTER_URL}
							alt=""
							fill
							priority
							fetchPriority="high"
							sizes="352px"
							className="object-cover"
						/>
						<span className="absolute inset-0 flex items-center justify-center bg-slate-950/10 transition-colors group-hover:bg-slate-950/0">
							<span className="flex size-12 items-center justify-center rounded-full bg-white shadow-lg transition-transform group-hover:scale-110">
								<Play className="ml-0.5 size-5 fill-neutral-950 text-neutral-950" />
							</span>
						</span>
					</button>
				</div>
			) : (
				<div className="relative aspect-video w-full max-w-4xl">
					<iframe
						ref={iframeRef}
						onLoad={handleIframeLoad}
						className="h-full w-full overflow-hidden rounded-xl border border-slate-200 bg-black shadow-[0_24px_60px_-28px_rgba(15,23,42,0.45)]"
						src={`${YOUTUBE_ORIGIN}/embed/${PRODUCT_DEMO_VIDEO_ID}?rel=0&autoplay=1&enablejsapi=1`}
						title="Binary ATS product demo"
						allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
						referrerPolicy="strict-origin-when-cross-origin"
						allowFullScreen
					/>
				</div>
			)}
		</section>
	);
};
